/**
 * DrillDownDrawer — Right-side slide-in panel for drill-down detail.
 *
 * Rendered once inside DrillDownProvider. Subscribes to DrillDownContext.
 * Shows source records behind the clicked KPI card.
 *
 * Features:
 * - Title, KPI value, source module badge
 * - Breadcrumb navigation
 * - Search + column filters
 * - Sortable table with pagination
 * - CSV/Excel export
 * - "Open source record" link
 * - Sensitive data audit notice
 * - Role-based access guard
 * - Reconciliation total vs KPI value
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  X, Loader2, AlertCircle, Download, ExternalLink,
  ChevronRight, Shield, Inbox, Search, RefreshCw,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import { useDrillDown } from './DrillDownContext';
import { METRIC_REGISTRY, getMetricTitle, type MetricModule } from '../../lib/dashboardDrilldownRegistry';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { exportToExcel } from '../../lib/utils/importExport';
import type { DrilldownResult } from '../../lib/queries/drilldowns';

// ── Helpers ───────────────────────────────────────────────────

function fmtCell(value: unknown, format?: string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'currency') {
    return `SAR ${Number(value).toLocaleString('en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (format === 'number') return Number(value).toLocaleString('en-SA');
  if (format === 'date') {
    try { return new Date(String(value)).toLocaleDateString('en-SA', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return String(value); }
  }
  if (format === 'percent') return `${Number(value).toFixed(1)}%`;
  return String(value);
}

// Column/total labels come from the query loaders as plain English strings.
// Rather than touch every loader, we translate them here by slugifying the
// English label into a lookup key — must match scripts/i18n generation.
function slugifyLabel(label: string): string {
  return label.toLowerCase().replace(/#/g, 'no').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function sortRows(
  rows: Record<string, unknown>[],
  key: string,
  dir: 'asc' | 'desc',
): Record<string, unknown>[] {
  return [...rows].sort((a, b) => {
    const av = a[key]; const bv = b[key];
    const an = Number(av); const bn = Number(bv);
    const numComp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av ?? '').localeCompare(String(bv ?? ''));
    return dir === 'asc' ? numComp : -numComp;
  });
}

const PAGE_SIZES = [25, 50, 100];

const MODULE_FALLBACK: Record<MetricModule, string> = {
  finance: 'Finance', sales: 'Sales', hr: 'HR', payroll: 'Payroll',
  procurement: 'Procurement', warehouse: 'Warehouse', manufacturing: 'Manufacturing',
  projects: 'Projects', compliance: 'Compliance', approvals: 'Approvals',
  risk: 'Risk', zatca: 'ZATCA', collections: 'Collections', crm: 'CRM',
};

// ── Component ─────────────────────────────────────────────────

export default function DrillDownDrawer() {
  const { t } = useTranslation();
  const { session, isOpen, close, stepBack } = useDrillDown();
  const { profile } = useAuth();
  const { activeCompanyId } = useCompany();

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const searchRef = useRef<HTMLInputElement>(null);

  // The non-production environment banner is `position: sticky` with a very
  // high z-index so it always stays visible above modals/drawers. Since this
  // drawer is `position: fixed` (out of document flow), it doesn't naturally
  // start below the banner the way in-flow page content does — without this,
  // the banner paints over the drawer's own top padding/header. Measure the
  // banner's live height (it can wrap on narrow viewports) and offset below it.
  const [topOffset, setTopOffset] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    const banner = document.querySelector<HTMLElement>('[data-testid="environment-banner"]');
    if (!banner) { setTopOffset(0); return; }
    const update = () => setTopOffset(banner.getBoundingClientRect().height);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(banner);
    return () => ro.disconnect();
  }, [isOpen]);

  // Reset state when metric changes
  useEffect(() => {
    setSearch(''); setSortKey(null); setPage(1);
    setTimeout(() => searchRef.current?.focus(), 200);
  }, [session?.metricKey]);

  const metricKey = session?.metricKey ?? '';
  const def = METRIC_REGISTRY[metricKey];

  // Role check
  const hasAccess = !def || def.allowed_roles.includes(profile?.role ?? '');

  // Data fetching
  const { data, isLoading, error, refetch, dataUpdatedAt } = useQuery<DrilldownResult>({
    queryKey: ['drilldown', metricKey, activeCompanyId, session?.filters],
    queryFn: async () => {
      if (!def) throw new Error(`Unknown metric: ${metricKey}`);
      if (!hasAccess) throw new Error('You do not have permission to view this data');
      return def.loader(activeCompanyId, session?.filters as Record<string, unknown>);
    },
    enabled: isOpen && !!metricKey && hasAccess,
    staleTime: 60_000,
  });

  // Client-side filtering + sort + pagination
  const allRows = data?.rows ?? [];

  const filtered = search.trim()
    ? allRows.filter(row =>
        Object.values(row).some(v => String(v ?? '').toLowerCase().includes(search.toLowerCase()))
      )
    : allRows;

  const sorted = sortKey ? sortRows(filtered, sortKey, sortDir) : filtered;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
    setPage(1);
  };

  const handleExport = useCallback(() => {
    if (!data) return;
    const rows = sorted.map(r => {
      const out: Record<string, unknown> = {};
      for (const c of data.columns) out[c.label] = r[c.key];
      return out;
    });
    exportToExcel(rows, getMetricTitle(metricKey), `${metricKey}_drilldown`);
  }, [data, sorted, metricKey]);

  // Keyboard: Escape closes
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) close(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const title = def
    ? t(`drilldown.title_${metricKey}`, def.display_title)
    : getMetricTitle(metricKey);
  const subtitle = def?.subtitle
    ? t(`drilldown.subtitle_${metricKey}`, def.subtitle)
    : undefined;
  const moduleLabel = def?.module
    ? t(`drilldown.module_${def.module}`, MODULE_FALLBACK[def.module])
    : undefined;
  const isSensitive = def?.sensitive ?? false;
  const exportEnabled = def?.export_enabled ?? true;
  const routeToSource = def?.route_to_source;
  const crumbs = session?.breadcrumbs ?? [];
  const lastRefresh = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString('en-SA') : '—';

  const totalAmount = data?.total;
  const totalLabel = data?.totalLabel;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-x-0 bottom-0 bg-black/50 z-40 transition-opacity"
        style={{ top: topOffset }}
        onClick={close}
        aria-hidden="true"
      />

      {/* Drawer — slides in from the trailing (logical "end") edge: the
          right in LTR, the left in RTL, mirroring reading direction like
          the rest of the app's side panels.
          `top` is offset below the (higher z-index) environment banner so
          it never paints over the drawer's own header. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ top: topOffset }}
        className="fixed bottom-0 end-0 w-full max-w-3xl bg-slate-50 dark:bg-slate-950 border-s border-slate-200 dark:border-slate-800 z-50 flex flex-col shadow-2xl animate-slide-in-right overflow-hidden"
      >
        {/* ── Header ───────────────────────────────────────────── */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0 gap-3">
          <div className="flex-1 min-w-0">
            {/* Breadcrumb */}
            {crumbs.length > 0 && (
              <nav className="flex items-center gap-1 mb-1.5 flex-wrap" aria-label={t('drilldown.breadcrumb_aria', 'breadcrumb')}>
                {crumbs.map((crumb, i) => (
                  <span key={i} className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        // Step back to this breadcrumb level
                        for (let j = crumbs.length - 1; j >= i; j--) stepBack();
                      }}
                      className="text-[11px] text-slate-600 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {crumb.label}
                    </button>
                    <ChevronRight className="w-3 h-3 text-slate-800 dark:text-slate-700 rtl:rotate-180" />
                  </span>
                ))}
              </nav>
            )}

            {/* Title */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-slate-900 dark:text-white font-semibold text-base leading-tight">{title}</h2>
              {moduleLabel && (
                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 uppercase tracking-wide">
                  {moduleLabel}
                </span>
              )}
              {isSensitive && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20">
                  <Shield className="w-3 h-3" /> {t('drilldown.sensitive_badge', 'Sensitive')}
                </span>
              )}
            </div>
            {subtitle && <p className="text-xs text-slate-600 dark:text-slate-500 mt-0.5">{subtitle}</p>}

            {/* KPI value context */}
            {session?.kpiValue !== undefined && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t('drilldown.showing_records_behind', 'Showing records behind:')}{' '}
                <span className="font-semibold text-slate-900 dark:text-white">{session.kpiValue}</span>
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            {exportEnabled && data && data.rows.length > 0 && (
              <button
                onClick={handleExport}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-lg text-xs transition-colors"
                title={t('drilldown.export_title', 'Export to Excel')}
              >
                <Download className="w-3.5 h-3.5" /> {t('drilldown.export', 'Export')}
              </button>
            )}
            {routeToSource && (
              <a
                href={routeToSource}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-700 text-blue-600 dark:text-blue-400 rounded-lg text-xs transition-colors"
                title={t('drilldown.open_module_title', 'Open source module')}
              >
                <ExternalLink className="w-3.5 h-3.5" /> {t('drilldown.open_module', 'Open Module')}
              </a>
            )}
            <button
              onClick={() => refetch()}
              className="p-1.5 text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              title={t('drilldown.refresh_title', 'Refresh')}
              disabled={isLoading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={close}
              className="p-1.5 text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              aria-label={t('drilldown.close_aria', 'Close drill-down')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Sensitive data notice ────────────────────────────── */}
        {isSensitive && (
          <div className="flex items-start gap-2 px-5 py-2 bg-amber-100 dark:bg-amber-500/8 border-b border-amber-300 dark:border-amber-500/20 text-xs text-amber-800 dark:text-amber-300 shrink-0">
            <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {t('drilldown.sensitive_notice', 'This data is sensitive. Your access has been logged for audit purposes.')}
          </div>
        )}

        {/* ── Access denied ────────────────────────────────────── */}
        {!hasAccess && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-3">
              <Shield className="w-12 h-12 text-red-500/60 dark:text-red-400/60 mx-auto" />
              <p className="text-slate-900 dark:text-white font-medium">{t('drilldown.access_restricted', 'Access Restricted')}</p>
              <p className="text-sm text-slate-600 dark:text-slate-500">
                {t('drilldown.access_restricted_message', 'Your role ({{role}}) does not have permission to view this data.', {
                  role: profile?.role ?? t('drilldown.unknown_role', 'unknown'),
                })}
              </p>
            </div>
          </div>
        )}

        {hasAccess && (
          <>
            {/* ── Search + filters bar ─────────────────────────── */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200 dark:border-slate-800 shrink-0">
              <div className="relative flex-1">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 dark:text-slate-500 pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder={t('drilldown.search_placeholder', 'Search records…')}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg ps-9 pe-3 py-1.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute end-2 top-1/2 -translate-y-1/2 text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <select
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-lg px-2 py-1.5 text-xs"
              >
                {PAGE_SIZES.map(s => <option key={s} value={s}>{t('drilldown.rows_option', { count: s })}</option>)}
              </select>
            </div>

            {/* ── Body ─────────────────────────────────────────── */}
            <div className="flex-1 overflow-auto">
              {isLoading && (
                <div className="flex items-center justify-center py-20 gap-2 text-slate-500 dark:text-slate-400 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin" /> {t('drilldown.loading_records', 'Loading records…')}
                </div>
              )}

              {error && !isLoading && (
                <div className="m-5 flex items-start gap-2 px-4 py-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl text-sm text-red-700 dark:text-red-300">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">{t('drilldown.failed_to_load', 'Failed to load records')}</p>
                    <p className="text-xs text-red-600/80 dark:text-red-400/80 mt-0.5">
                      {error instanceof Error ? error.message : String(error)}
                    </p>
                  </div>
                </div>
              )}

              {!isLoading && !error && filtered.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-slate-600 dark:text-slate-500">
                  <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                    <Inbox className="w-6 h-6 text-slate-400 dark:text-slate-500" />
                  </div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{t('drilldown.no_records_found', 'No records found')}</p>
                  {search
                    ? <p className="text-xs mt-1">{t('drilldown.clear_search_hint', 'Try clearing the search filter')}</p>
                    : <p className="text-xs mt-1">{t('drilldown.no_records_hint', "There's no data behind this metric yet")}</p>}
                </div>
              )}

              {!isLoading && !error && data && pageRows.length > 0 && (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white dark:bg-slate-900 z-10 border-b border-slate-200 dark:border-slate-800">
                    <tr>
                      {data.columns.map(col => {
                        const isNum = col.format === 'currency' || col.format === 'number';
                        const isSorted = sortKey === col.key;
                        return (
                          <th
                            key={col.key}
                            onClick={() => handleSort(col.key)}
                            className={`px-4 py-2.5 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-900 dark:hover:text-white transition-colors select-none ${isNum ? 'text-end' : 'text-start'}`}
                          >
                            <span className="inline-flex items-center gap-1">
                              {t(`drilldown.col_${slugifyLabel(col.label)}`, col.label)}
                              {isSorted && (
                                sortDir === 'asc'
                                  ? <ChevronUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                  : <ChevronDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                              )}
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row, i) => (
                      <tr
                        key={i}
                        className="border-b border-slate-200/50 dark:border-slate-800/50 hover:bg-slate-100/30 dark:hover:bg-slate-800/30 transition-colors group"
                      >
                        {data.columns.map(col => {
                          const isNum = col.format === 'currency' || col.format === 'number';
                          const val = row[col.key];
                          const isNegative = isNum && Number(val ?? 0) < 0;
                          return (
                            <td
                              key={col.key}
                              className={`px-4 py-2 text-xs ${isNum ? 'text-end tabular-nums' : ''} ${
                                isNegative ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-200'
                              }`}
                            >
                              {fmtCell(val, col.format)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* ── Footer ───────────────────────────────────────── */}
            {data && (
              <div className="border-t border-slate-200 dark:border-slate-800 px-5 py-3 flex items-center justify-between shrink-0 gap-4 flex-wrap">
                {/* Stats */}
                <div className="flex items-center gap-4 text-xs text-slate-600 dark:text-slate-500">
                  <span>
                    {search
                      ? t('drilldown.records_count_filtered', { count: sorted.length, total: allRows.length })
                      : t('drilldown.records_count', { count: sorted.length })}
                  </span>
                  <span>{t('drilldown.refreshed_at', 'Refreshed {{time}}', { time: lastRefresh })}</span>
                </div>

                {/* Total */}
                {totalAmount !== undefined && totalLabel && (
                  <span className="text-xs font-semibold text-slate-900 dark:text-white">
                    {t(`drilldown.total_${slugifyLabel(totalLabel)}`, totalLabel)}:{' '}
                    <span className="text-emerald-600 dark:text-emerald-400">
                      SAR {totalAmount.toLocaleString('en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </span>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-2.5 py-1 text-xs bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded disabled:opacity-40 rtl:rotate-180"
                    >
                      ‹
                    </button>
                    <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">{page} / {totalPages}</span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-2.5 py-1 text-xs bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded disabled:opacity-40 rtl:rotate-180"
                    >
                      ›
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
